import React, { useMemo, useState, useRef, useEffect } from 'react';
import HoverCard from '../../../components/HoverCard.jsx';
import './MessagesContainer.css';
import EmojiPicker from 'emoji-picker-react';
import AudioPlayer from '../../../components/chatdock/AudioPlayer';

const emojiOptions = ['👍', '😂', '❤️', '😮', '😢', '👏'];

const MessagesContainer = ({
  chatMessages = [],
  reactions = [],
  onAddReaction,
  onDeleteMessage,
  onEditMessage,
  userId,
  isGroupThread = false,
  senderProfiles = {},
  onScroll,
  onReply
}) => {
  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [reactionOpenFor, setReactionOpenFor] = useState(null);
  const containerRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatMessages.length]);

  // No document-level handler needed - we use a backdrop overlay instead

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    onDeleteMessage?.(messageId);
  };

  const handleStartEdit = (msg) => {
    if (msg.media_url) return;
    setEditingMessageId(msg.id);
    setEditText(msg.content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId) return;
    onEditMessage?.(editingMessageId, editText);
    setEditingMessageId(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  // Helper: get reactions for a message (from embedded message_reactions or fallback to reactions prop)
  const getReactions = (msg) =>
    msg.message_reactions || reactions.filter(r => r.message_id === msg.id);
    
  // Check if user has already reacted with an emoji to a message
  const hasUserReacted = (messageId, emoji) => {
    return reactions.some(r => 
      r.message_id === messageId && 
      r.user_id === userId && 
      r.emoji === emoji
    );
  };

  // Build a list with date separators
  const items = useMemo(() => {
    const out = [];
    let prevDay = '';
    const safeMessages = Array.isArray(chatMessages) ? chatMessages : [];
    safeMessages.forEach((m) => {
      const d = new Date(m.created_at);
      const dayKey = d.toDateString();
      if (dayKey !== prevDay) {
        out.push({ _kind: 'separator', key: `sep-${dayKey}-${d.getTime()}`, label: d.toLocaleDateString() });
        prevDay = dayKey;
      }
      out.push({ _kind: 'message', key: m.id, data: m });
    });
    return out;
  }, [chatMessages]);

  return (
    <div className="messages-container" ref={containerRef} onScroll={onScroll}>
      {chatMessages.length === 0 && (
        <div style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)'
        }}>
          <div>
            <div style={{ fontSize: 18, marginBottom: 6 }}>No messages yet</div>
            <div style={{ fontSize: 14 }}>Start the conversation below.</div>
          </div>
        </div>
      )}
      {items.map((item) => {
        if (item._kind === 'separator') {
          return (
            <div key={item.key} className="day-separator">
              <span>{item.label}</span>
            </div>
          );
        }
        const msg = item.data;
        const isOwn = msg.sender_id === userId;
        const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const senderName = isGroupThread && !isOwn ? (senderProfiles[msg.sender_id]?.name || senderProfiles[msg.sender_id]?.email || 'Member') : (isOwn ? 'You' : null);
        return (
          <div key={item.key} id={msg.id ? `msg-${msg.id}` : undefined} className={`message-row ${isOwn ? 'own' : ''}`}>
            <div className="message-wrapper">
            {isGroupThread && !isOwn && (
              <HoverCard type="profile" context="messenger" data={{
                id: msg.sender_id,
                display_name: senderProfiles[msg.sender_id]?.name || senderProfiles[msg.sender_id]?.email || 'Member',
                avatar_url: senderProfiles[msg.sender_id]?.avatar_url || null,
                email: senderProfiles[msg.sender_id]?.email || null
              }}>
                <div className="sender-avatar">
                  {senderProfiles[msg.sender_id]?.avatar_url ? (
                    <img src={senderProfiles[msg.sender_id].avatar_url} alt="" />
                  ) : (
                    <span>{(senderName || 'M').slice(0,1).toUpperCase()}</span>
                  )}
                </div>
              </HoverCard>
            )}
            <div className="message-bubble" onClick={(e) => { if (menuOpenFor) { e.stopPropagation(); setMenuOpenFor(null); } }}>
              {/* Ellipsis menu at top-right corner for own messages */}
              {isOwn && msg.id && editingMessageId !== msg.id && (
                <span className="message-menu" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="message-ellipsis"
                    onClick={() => setMenuOpenFor(menuOpenFor === msg.id ? null : msg.id)}
                    title="More options"
                  >
                    ⋯
                  </button>
                  {menuOpenFor === msg.id && (
                    <div className="message-dropdown">
                      {!msg.media_url && (
                        <button onClick={() => { setMenuOpenFor(null); handleStartEdit(msg); }}>
                          ✎ Edit
                        </button>
                      )}
                      <button className="delete" onClick={() => { setMenuOpenFor(null); handleDeleteMessage(msg.id); }}>
                        🗑 Delete
                      </button>
                    </div>
                  )}
                </span>
              )}
              {senderName && (
                <HoverCard type="profile" context="messenger" data={{
                  id: msg.sender_id,
                  display_name: senderProfiles[msg.sender_id]?.name || senderProfiles[msg.sender_id]?.email || 'Member',
                  avatar_url: senderProfiles[msg.sender_id]?.avatar_url || null,
                  email: senderProfiles[msg.sender_id]?.email || null
                }}>
                  <div className="sender-label" role="button" tabIndex={0}>{senderName}</div>
                </HoverCard>
              )}
              {editingMessageId === msg.id ? (
                <div className="message-edit-form">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') handleCancelEdit(); }}
                    autoFocus
                  />
                  <div className="message-edit-actions">
                    <button onClick={handleSaveEdit} title="Save">✓</button>
                    <button onClick={handleCancelEdit} title="Cancel">✕</button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Reply reference if this message is a reply */}
                  {msg.reply_to_id && (() => {
                    const replyMsg = chatMessages.find(m => m.id === msg.reply_to_id);
                    return replyMsg ? (
                      <div className="message-reply-ref" onClick={() => {
                        const el = document.getElementById(`msg-${msg.reply_to_id}`);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}>
                        ↩ {replyMsg.content?.slice(0, 40)}{replyMsg.content?.length > 40 ? '…' : ''}
                      </div>
                    ) : null;
                  })()}
                  {msg.content && !msg.media_url && <span>{msg.content}</span>}
                  {msg.media_url && (() => {
                    const url = msg.media_url;
                    const urlLower = url.toLowerCase();
                    const contentLower = (msg.content || '').toLowerCase();
                    // Check content label first for audio/video to handle .webm files correctly
                    const isAud = contentLower === 'audio message' || /(\.mp3|\.wav|\.m4a|\.aac)/i.test(urlLower);
                    const isVid = contentLower === 'video message' || (/(\.mp4|\.webm|\.mov|\.m4v)/i.test(urlLower) && !isAud);
                    const isImg = /(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.bmp|\.svg)/i.test(urlLower) || url.includes('giphy.com');
                    if (isImg) return <img className="message-img" src={url} alt={msg.content || 'image'} style={{ maxWidth: '100%', borderRadius: 8 }} />;
                    if (isAud) return <AudioPlayer src={url} />;
                    if (isVid) return <video className="message-video" controls src={url} style={{ maxWidth: '100%', borderRadius: 8 }} />;
                    return <a href={url} target="_blank" rel="noopener noreferrer">{msg.content || 'View attachment'}</a>;
                  })()}
                  {msg.edited_at && <span className="message-edited">(edited)</span>}
                </>
              )}
              {/* Display existing reactions only */}
              {getReactions(msg).length > 0 && (
                <div className="message-reactions">
                  {getReactions(msg).map((r, idx) => (
                    <span key={r.id || idx} className="reaction-emoji">
                      {r.emoji}
                    </span>
                  ))}
                </div>
              )}
              <span className="message-time">
                {time}
                {isOwn && (
                  <span className={`message-status ${msg.read_at ? 'read' : msg.delivered_at ? 'delivered' : msg.id ? 'sent' : 'pending'}`}>
                    {msg.read_at ? '✓✓' : msg.delivered_at ? '✓✓' : msg.id ? '✓' : '○'}
                  </span>
                )}
              </span>
            </div>
              {/* Quick actions: React + Reply icons */}
              <div className="message-quick-actions">
                <button
                  className="message-quick-action"
                  title="React"
                  onClick={(e) => { e.stopPropagation(); setReactionOpenFor(reactionOpenFor === msg.id ? null : msg.id); }}
                >
                  😊
                </button>
                <button
                  className="message-quick-action"
                  title="Reply"
                  onClick={(e) => { e.stopPropagation(); onReply?.(msg); }}
                >
                  ↩
                </button>
              </div>
              {/* Reactions popup */}
              {reactionOpenFor === msg.id && (
                <>
                  {/* Invisible backdrop to close popup on outside click */}
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 99999 }}
                    onClick={() => setReactionOpenFor(null)}
                  />
                  <div
                    className="message-reactions-popup"
                    style={{ position: 'absolute', zIndex: 100001 }}
                  >
                    {['👍', '😊', '❤️', '😮', '😢', '👏'].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="message-reaction-btn"
                        style={{ position: 'relative', zIndex: 100002, cursor: 'pointer' }}
                        title={`React with ${emoji}`}
                        onClick={() => {
                          console.debug('[Messenger] reaction click', { messageId: msg.id, emoji });
                          setReactionOpenFor(null);
                          onAddReaction?.(msg.id, emoji);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessagesContainer;